import classNames from 'classnames';
import React from 'react';
import * as AspectRatio from '@radix-ui/react-aspect-ratio';
import { IMAGE_TRANSFORMER_MAP, ImageFitType, ImageObjectFitType, ImageTransformType, ImageType } from './constants';
import styles from './image.module.scss';
import { ImageSizeDefinition, isExtensiveImageSizeDefinition } from './types';
import { makeSourceBuilder } from './source-builder';

type BaseProps = {
  alt?: string;
  imageSizes: ImageTransformType | ImageSizeDefinition;
  baseSrc: string;
  className?: string;
  loading?: 'lazy' | 'eager';
};

type ImageVariantProps =
  | {
      type: ImageType.aspectRatio;
    }
  | { type: ImageType.fillContainer; objectFit?: ImageObjectFitType };

type Props = ImageVariantProps & BaseProps;

export const extractDecimalAspectRatio = (aspectRatio: string): number => {
  const [first = '1', second = '1'] = aspectRatio.split(':');

  return parseInt(first) / parseInt(second);
};

/**
 * Reusable image component for responsive lazy-loaded images
 * @param props.baseSrc base path with handle
 * @param props.type defines type of image. Either you have a fixed aspect ratio or you can make the image fill the parent container, similar to a background image
 * @param props.objectFit optional for type fillContainer. Defines how the image fills out the parent container
 * @param props.alt alternative text, may be undefined for presentation images
 * @param props.imageSizes define responsive image sizes (see: IMAGE_TRANSFORM_TYPES) or pass size definition directly
 * @param props.className optional class name
 * @param props.loading optional loading strategy attribute, default is lazy
 * @constructor
 */
export const Image: React.FC<Props> = (props) => {
  const { type, baseSrc, alt, imageSizes, className, loading = 'lazy' } = props;
  const isAspectRatio = type === ImageType.aspectRatio;
  const imageSizeMap = typeof imageSizes === 'string' ? IMAGE_TRANSFORMER_MAP[imageSizes] : imageSizes;
  const createSource = isAspectRatio
    ? makeSourceBuilder({
        src: baseSrc,
        type,
        aspectRatio: imageSizeMap.aspectRatio,
        fit: ImageFitType.crop,
      })
    : makeSourceBuilder({
        src: baseSrc,
        type,
      });

  const pictureElement = (
    <picture
      className={classNames(styles.root, {
        [styles.fillContainer]: !isAspectRatio,
        [styles.contain]: !isAspectRatio && props.objectFit === ImageObjectFitType.cover,
      })}
    >
      {isExtensiveImageSizeDefinition(imageSizeMap) ? (
        <>
          <source media="screen and (min-width:1746px)" srcSet={createSource({ width: imageSizeMap.min1746 })} />
          <source
            media="screen and (min-width:1441px) and (max-width:1745px)"
            srcSet={createSource({ width: imageSizeMap.max1745 })}
          />
          <source
            media="screen and (min-width:1201px) and (max-width:1440px)"
            srcSet={createSource({ width: imageSizeMap.max1440 })}
          />
          <source
            media="screen and (min-width:961px) and (max-width:1200px)"
            srcSet={createSource({ width: imageSizeMap.max1200 })}
          />
          <source
            media="screen and (min-width:641px) and (max-width:960px)"
            srcSet={createSource({ width: imageSizeMap.max960 })}
          />
          <source media="screen and (max-width:640px)" srcSet={createSource({ width: imageSizeMap.max640 })} />
          <img
            alt={alt ?? ''}
            className={classNames(styles.image, className)}
            loading={loading}
            src={createSource({ width: imageSizeMap.min1746, withoutRetina: true })}
          />
        </>
      ) : (
        <>
          <source srcSet={createSource({ width: imageSizeMap.width })} />
          <img
            alt={alt ?? ''}
            className={classNames(styles.image, className)}
            loading={loading}
            src={createSource({ width: imageSizeMap.width, withoutRetina: true })}
          />
        </>
      )}
    </picture>
  );

  if (isAspectRatio) {
    return (
      <AspectRatio.Root ratio={extractDecimalAspectRatio(imageSizeMap.aspectRatio)}>{pictureElement}</AspectRatio.Root>
    );
  }

  return pictureElement;
};
